import { Datepicker, locale } from "@mobiscroll/react";
import { Button, Card, CardProps, Grid2, IconButton, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import useTimeFormat from "../../../../../hooks/useTimeFormat/useTimeFormat";
import { handleStartDateChange } from "../../menu-timeframes.functions";
import { initialStartDate } from "../NewTimeframeCreator";
import { FloorInventory } from "../../../typings/floor-inventory.entity";
import { Timeframe } from "../../../typings/shared/timeframe";

//  name: "StyledTimeframeNewItemCard"
export const StyledTimeframeNewItemCard: React.FC<CardProps> = ({ children, sx, ...rest }) => (
  <Card
    sx={{
      width: 420,
      height: 185,
      padding: "16px",
      margin: "10px 10px 16px 4px",
      ...sx
    }}
    {...rest}
  >
    {children}
  </Card>
);

type NewTimeframeItemProps = {
  selectedFloor: FloorInventory;
  floorTimeframes: Timeframe[];
  startDate: DateTime | null;
  setNewTimeframe: (tf: boolean) => void;
  setStartDate: React.Dispatch<React.SetStateAction<DateTime | null>>;
  setShowTimeframeDetail: (d: string | null) => void;
  clickNextCreate: (startDay: string) => void;
};

export function NewTimeframeItem({
  selectedFloor,
  floorTimeframes,
  startDate,
  setNewTimeframe,
  setStartDate,
  setShowTimeframeDetail,
  clickNextCreate
}: Readonly<NewTimeframeItemProps>) {
  const { t, i18n } = useTranslation();
  const { dateFormat } = useTimeFormat();

  const clearTimeframe = () => {
    setStartDate(null);
    setNewTimeframe(false);

    // if none of floor time frames existing, set null by default
    const defaultStart =
      floorTimeframes.length > 0 &&
      floorTimeframes[0].start &&
      DateTime.fromISO(floorTimeframes[0].start).isValid
        ? DateTime.fromISO(floorTimeframes[0].start).toFormat(dateFormat)
        : null;
    setShowTimeframeDetail(defaultStart);
  };

  return (
    <>
      {/* floor section */}
      <StyledTimeframeNewItemCard>
        <Grid2 container data-testid="new-timeframe-floor-container">
          {/* header of the selected floor */}
          <Grid2 container sx={{ width: "100%", height: "80%", justifyContent: "space-between" }}>
            <Grid2>
              <Typography variant={"h6"}>{selectedFloor.name}</Typography>
              <Typography variant={"subtitle1"}>{selectedFloor.level}</Typography>
            </Grid2>
            {/* clear new timeframe button */}
            <Grid2>
              <IconButton onClick={clearTimeframe} data-testid="clear-btn">
                <Close />
              </IconButton>
            </Grid2>
          </Grid2>
          <Grid2
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            <Datepicker
              data-testid={"new-timeframe-input"}
              min={initialStartDate(floorTimeframes, dateFormat)}
              id="date-picker-inline"
              label={t("Start date")}
              value={startDate?.toFormat(dateFormat) ?? ""}
              onChange={ev => handleStartDateChange(ev.value as Date, setStartDate)}
              dateFormat={dateFormat.toUpperCase()}
              locale={locale[i18n.language]}
            />

            <Button
              sx={{ mt: 2 }}
              data-testid="new-timeframe-next-create-btn"
              disabled={!startDate || startDate.valueOf() < Date.now()}
              onClick={() => {
                /**
                 * fetch new date when editing a time
                 * when it cannot be null, if it is, button is disabled
                 */
                const startDay = (startDate as DateTime).toFormat("yyyy-MM-dd");
                clickNextCreate(startDay);
              }}
            >
              {t("Create")}
            </Button>
          </Grid2>
        </Grid2>
      </StyledTimeframeNewItemCard>
    </>
  );
}
